import { useState, useMemo } from 'react';
import { ItemSlot }          from '../atoms/ItemSlot';
import { getSellPrice }      from '../../data/items';

// Rarity filter tabs — mirrors InventoryGrid's FILTER_TABS style
const RUNE_FILTER_TABS = [
  { id: 'all',       label: 'All'         },
  { id: 'common',    label: '⬦ Common'    },
  { id: 'uncommon',  label: '⬦ Uncommon'  },
  { id: 'rare',      label: '⬦ Rare'      },
  { id: 'epic',      label: '⬦ Epic'      },
  { id: 'legendary', label: '⭐ Legendary' },
];

const RARITY_ORDER = { legendary: 0, epic: 1, rare: 2, uncommon: 3, common: 4 };

/**
 * Stacked rune inventory — shown when the character screen is in "Runes" view.
 * Layout matches InventoryGrid exactly: same header, filter tabs, sort toggles,
 * sell mode, sell-all bar, and 10-column inventory-grid using ItemSlot atoms.
 *
 * Identical rune names are collapsed into one slot with a ×N count badge.
 * Clicking a slot equips one copy (or sells one in sell mode).
 */
export function RuneInventoryGrid({ inventory, onEquip, onSell, onSellAllRunes, totalGold, playerLevel = 1 }) {
  const [filter,         setFilter]         = useState('all');
  const [sellMode,       setSellMode]       = useState(false);
  const [sortMode,       setSortMode]       = useState('none'); // 'none' | 'rarity' | 'level'
  const [confirmSellAll, setConfirmSellAll] = useState(false);

  // ── Group runes by name into stacks ──────────────────────────────────────
  const allStacks = useMemo(() => {
    const groups = {};
    inventory.forEach((item, idx) => {
      if (!item || item.slotType !== 'rune') return;
      if (!groups[item.name]) {
        groups[item.name] = { rune: item, indices: [], count: 0 };
      }
      groups[item.name].indices.push(idx);
      groups[item.name].count++;
    });
    return Object.values(groups);
  }, [inventory]);

  // ── Sort ──────────────────────────────────────────────────────────────────
  const sortedStacks = useMemo(() => {
    const s = [...allStacks];
    if (sortMode === 'rarity') {
      s.sort((a, b) => (RARITY_ORDER[a.rune.rarity] ?? 4) - (RARITY_ORDER[b.rune.rarity] ?? 4));
    } else if (sortMode === 'level') {
      s.sort((a, b) => (b.rune.levelReq ?? 1) - (a.rune.levelReq ?? 1));
    }
    return s;
  }, [allStacks, sortMode]);

  // ── Filter by rarity ──────────────────────────────────────────────────────
  const filteredStacks = useMemo(() => {
    if (filter === 'all') return sortedStacks;
    return sortedStacks.filter(s => s.rune.rarity === filter);
  }, [sortedStacks, filter]);

  // ── Pad to next multiple of 10 for grid alignment ─────────────────────────
  const gridItems = useMemo(() => {
    const items = [...filteredStacks];
    const rem = items.length % 10;
    if (rem !== 0) for (let i = 0; i < 10 - rem; i++) items.push(null);
    return items;
  }, [filteredStacks]);

  const totalRunes    = inventory.filter(i => i?.slotType === 'rune').length;
  const sellableCount = filteredStacks.filter(s =>
    s.indices.some(i => !inventory[i]?.locked)
  ).length;

  // ── Toggle sort (cycle through, same as InventoryGrid) ────────────────────
  const toggleSort = (mode) => setSortMode(prev => prev === mode ? 'none' : mode);

  // ── Toggle sell mode ──────────────────────────────────────────────────────
  const toggleSellMode = () => {
    setSellMode(v => !v);
    setConfirmSellAll(false);
  };

  // ── Item click ────────────────────────────────────────────────────────────
  const handleSlotClick = (stack) => {
    if (!stack) return;
    if (sellMode) {
      const sellIdx = stack.indices.find(i => !inventory[i]?.locked);
      if (sellIdx !== undefined) onSell(inventory[sellIdx], sellIdx);
    } else {
      onEquip(stack.rune, stack.indices[0]);
    }
  };

  // ── Sell All: two-click confirm ───────────────────────────────────────────
  const handleSellAllClick = () => {
    if (!confirmSellAll) {
      setConfirmSellAll(true);
    } else {
      onSellAllRunes?.();
      setConfirmSellAll(false);
      setSellMode(false);
    }
  };

  const filterLabel = filter === 'all' ? '' : ` ${filter}`;

  return (
    <div className="inventory-section">
      {/* ── Header row ── */}
      <div className="inventory-header">
        <h3 className="inventory-title">
          Runes{' '}
          <span className="inventory-count">
            {totalRunes} owned · {allStacks.length} type{allStacks.length !== 1 ? 's' : ''}
          </span>
        </h3>
        <div className="inventory-header-right">
          <button
            className={`inv-sort-btn${sortMode === 'rarity' ? ' inv-sort-btn--active' : ''}`}
            onClick={() => toggleSort('rarity')}
            title="Sort by rarity"
          >
            {sortMode === 'rarity' ? '✦ Rarity' : '⬦ Rarity'}
          </button>
          <button
            className={`inv-sort-btn${sortMode === 'level' ? ' inv-sort-btn--active' : ''}`}
            onClick={() => toggleSort('level')}
            title="Sort by level"
          >
            {sortMode === 'level' ? '▲ Level' : '⬦ Level'}
          </button>
          {sellMode && (
            <span className="inventory-gold-balance">💰 {totalGold}g</span>
          )}
          <button
            className={`sell-mode-btn${sellMode ? ' sell-mode-btn--active' : ''}`}
            onClick={toggleSellMode}
          >
            {sellMode ? '✅ Selling' : '💰 Sell'}
          </button>
        </div>
      </div>

      {/* ── Filter tabs ── */}
      <div className="inventory-filters">
        {RUNE_FILTER_TABS.map(tab => (
          <button
            key={tab.id}
            className={`inv-filter-btn${filter === tab.id ? ' inv-filter-btn--active' : ''}`}
            onClick={() => { setFilter(tab.id); setConfirmSellAll(false); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Sell-all bar ── */}
      {sellMode && (
        <div className="sell-all-bar">
          <span className="sell-all-count">
            {sellableCount} rune type{sellableCount !== 1 ? 's' : ''} in view
          </span>
          <button
            className={`sell-all-btn${confirmSellAll ? ' sell-all-btn--confirm' : ''}`}
            onClick={handleSellAllClick}
            disabled={sellableCount === 0}
          >
            {confirmSellAll
              ? `⚠️ Confirm sell all${filterLabel} runes (${totalRunes})`
              : `🗑 Sell All${filterLabel} Runes`}
          </button>
        </div>
      )}

      {/* ── Grid ── */}
      <div className="inventory-grid">
        {gridItems.map((stack, i) => {
          const price = (stack && sellMode) ? getSellPrice(stack.rune) : null;
          return (
            <div key={i} className="rune-stack-cell">
              <ItemSlot
                item={stack?.rune ?? null}
                index={i}
                onEquip={() => handleSlotClick(stack)}
                sellMode={sellMode && !!stack}
                sellPrice={price}
                playerLevel={playerLevel}
              />
              {stack && stack.count > 1 && (
                <span className="rune-stack-badge">×{stack.count}</span>
              )}
            </div>
          );
        })}
      </div>

      {sellMode && (
        <p className="sell-mode-hint">
          Click any rune to sell one copy · or use Sell All to clear the current filter.
        </p>
      )}
    </div>
  );
}
